import { resolveMeterColor, useTranslation } from "@next-platform/client-common-core";
import definitionSegmentWeights from "@next-platform/client-common-core/rulebook/profile/definitionSegmentWeights";
import {
  DefinitionLevelList,
  formatDefinitionLevelRange,
  meterDriverRows,
  meterTypeSummary,
} from "@next-platform/client-common-core/rulebook/profile/meterDefinitionRows";
import { Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { type ClockMeter, type MeterDefinition, type MeterLevel, MeterType } from "@next-platform/packages-ts-types";
import { memo } from "react";

import { Col } from "@/components";
import RulebookLevelSegments from "@/components/Rulebook/RulebookLevelSegments";
import RulebookTokenText from "@/components/Rulebook/RulebookTokenText";
import type { RenderTokenChip } from "@/components/Rulebook/tokenChipTint";
import Trans from "@/components/Trans/Trans";
import { useMeterColorTheme } from "@/hooks";

import RulebookProfileMeterCardHeader from "./RulebookProfileMeterCardHeader";
import RulebookProfileMeterLevelRow from "./RulebookProfileMeterLevelRow";
import RulebookProfileSectionHeading from "./RulebookProfileSectionHeading";
import useProfileTokenRenderer from "./useProfileTokenRenderer";

type Translate = (key: string, values?: Record<string, unknown>) => string;

const DRIVER_ARROW: Record<"up" | "down" | "flat", string> = { up: "▲", down: "▼", flat: "·" };

const DRIVER_ARROW_CLASS: Record<"up" | "down" | "flat", string> = { up: "text-green-9", down: "text-red-9", flat: "text-gray-10" };

const DriverRow = ({
  direction,
  text,
  fullText,
  renderChip,
}: {
  direction: "up" | "down" | "flat";
  text: string;
  fullText?: string;
  renderChip: RenderTokenChip;
}) => (
  <div className="flex gap-2">
    <span className={`flex-none ${DRIVER_ARROW_CLASS[direction]}`}>{DRIVER_ARROW[direction]}</span>
    <Text variant={TextVariants.LABEL_SMALL_REGULAR} className="text-gray-12" {...(fullText ? { title: fullText } : {})}>
      <RulebookTokenText text={text} renderChip={renderChip} />
    </Text>
  </div>
);

const LevelTable = ({
  meter,
  levels,
  kind,
  translate,
  color,
  segmentTestId,
  renderChip,
}: {
  meter: MeterDefinition;
  levels: MeterLevel[];
  kind: DefinitionLevelList;
  translate: Translate;
  color?: string;
  segmentTestId: string;
  renderChip: RenderTokenChip;
}) => {
  if (!levels.length) {
    return null;
  }
  const segments = definitionSegmentWeights({ meter, levels, kind });
  return (
    <div className="flex flex-col overflow-hidden rounded-[10px] border border-solid border-gray-6 dark:border-gray-8">
      <RulebookLevelSegments segments={segments} color={color} data-testid={segmentTestId} />
      {levels.map((level, index) => (
        <RulebookProfileMeterLevelRow
          key={index}
          level={level}
          range={formatDefinitionLevelRange(meter, levels, index, kind, translate)}
          renderChip={renderChip}
          data-testid={`RulebookProfile-DefMeter-${meter.id}-Level-${index}`}
        />
      ))}
    </div>
  );
};

/** Clock meters carry two level lists (time-of-day + story day); value meters one; counters none. */
const MeterLevelTables = ({
  meter,
  translate,
  color,
  renderChip,
}: {
  meter: MeterDefinition;
  translate: Translate;
  color?: string;
  renderChip: RenderTokenChip;
}) => {
  if (meter.type === MeterType.CLOCK) {
    const clock = meter as ClockMeter;
    return (
      <>
        <LevelTable
          meter={meter}
          levels={clock.tod_levels}
          kind={DefinitionLevelList.TOD}
          translate={translate}
          color={color}
          renderChip={renderChip}
          segmentTestId={`RulebookProfile-LevelSegments-${meter.id}-tod`}
        />
        <LevelTable
          meter={meter}
          levels={clock.day_levels}
          kind={DefinitionLevelList.DAY}
          translate={translate}
          color={color}
          renderChip={renderChip}
          segmentTestId={`RulebookProfile-LevelSegments-${meter.id}-day`}
        />
      </>
    );
  }
  if (meter.type === MeterType.NUMBER) {
    return meter.levels.length > 0 ? (
      <LevelTable
        meter={meter}
        levels={meter.levels}
        kind={DefinitionLevelList.VALUE}
        translate={translate}
        color={color}
        renderChip={renderChip}
        segmentTestId={`RulebookProfile-LevelSegments-${meter.id}`}
      />
    ) : null;
  }
  const levels = (meter as Exclude<MeterDefinition, ClockMeter | { type: MeterType.NUMBER }>).levels;
  return (
    <LevelTable
      meter={meter}
      levels={levels}
      kind={DefinitionLevelList.VALUE}
      translate={translate}
      color={color}
      renderChip={renderChip}
      segmentTestId={`RulebookProfile-LevelSegments-${meter.id}`}
    />
  );
};

const DefinitionMeterCard = ({ meter, translate, renderChip }: { meter: MeterDefinition; translate: Translate; renderChip: RenderTokenChip }) => {
  const theme = useMeterColorTheme();
  const iconColor = resolveMeterColor({ color: meter.color, theme });
  const isVisible = meter.visible !== false;
  const drivers = meterDriverRows(meter, translate);

  return (
    <article
      className="flex flex-col gap-3 rounded-[14px] border border-solid border-gray-5 dark:border-gray-8 bg-gray-2 p-4"
      data-testid={`RulebookProfile-DefMeter-${meter.id}`}
    >
      <RulebookProfileMeterCardHeader
        icon={meter.icon}
        meterType={meter.type}
        color={iconColor}
        label={meter.label}
        typeLine={meterTypeSummary(meter, translate)}
        isShown={isVisible}
      />
      {drivers.length ? (
        <div className="flex flex-col gap-1.5">
          {drivers.map((driver, index) => (
            <DriverRow key={index} {...driver} renderChip={renderChip} />
          ))}
        </div>
      ) : null}
      <MeterLevelTables meter={meter} translate={translate} color={iconColor} renderChip={renderChip} />
    </article>
  );
};

type RulebookDefinitionMetersSectionProps = {
  meters: MeterDefinition[];
};

/**
 * Definition Meters section (definition_visible only): every meter — hidden included — with its
 * drivers and full level tables (labels, ranges, captions, model instructions).
 */
const RulebookDefinitionMetersSection = ({ meters }: RulebookDefinitionMetersSectionProps) => {
  const { t } = useTranslation();
  const renderChip = useProfileTokenRenderer({ definitionMeters: meters });

  return (
    <Col className="gap-3" dataTestId="RulebookProfile-DefMeters">
      <RulebookProfileSectionHeading
        title={<Trans i18nKey="rulebook:profile.meters" />}
        subtitle={<Trans i18nKey="rulebook:profile.metersSubtitle" />}
      />
      <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-2">
        {meters.map((meter) => (
          <DefinitionMeterCard key={meter.id} meter={meter} translate={t} renderChip={renderChip} />
        ))}
      </div>
    </Col>
  );
};

export default memo(RulebookDefinitionMetersSection);
